/**
 * IconButton — Circular button for icon-only actions
 *
 * Variants:
 *   - variant: default, primary, destructive, ghost
 *   - size: sm, md, lg
 */
import { styled, XStack, type GetProps } from 'tamagui'

const IconButtonFrame = styled(XStack, {
  name: 'IconButton',
  role: 'button',
  ai: 'center',
  jc: 'center',
  br: '$lg',
  cursor: 'pointer',
  // Base bg — variants override
  bg: '$backgroundCard',

  variants: {
    variant: {
      default: {
        bg: '$backgroundCard',
        pressStyle: { opacity: 0.65, scale: 0.92 },
        hoverStyle: { opacity: 0.8 },
      },
      primary: {
        bg: '$colorPrimary',
        pressStyle: { opacity: 0.65, scale: 0.92 },
        hoverStyle: { opacity: 0.8 },
      },
      destructive: {
        bg: '$colorError',
        pressStyle: { opacity: 0.65, scale: 0.92 },
        hoverStyle: { opacity: 0.8 },
      },
      ghost: {
        bg: 'rgba(255,255,255,0)',
        pressStyle: { bg: '$backgroundSecondary', scale: 0.92 },
        hoverStyle: { bg: '$backgroundSecondary' },
      },
    },

    size: {
      sm: { w: 32, h: 32 },
      md: { w: 40, h: 40 },
      lg: { w: 48, h: 48 },
    },
  } as const,

  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

/**
 * Color map exported for use by IconButtonFab.
 * When using the primitive IconButton, you set icon color yourself.
 */
export const ICON_BUTTON_COLOR: Record<string, string> = {
  default: '$color',
  primary: '$colorInverse',
  destructive: '$colorInverse',
  ghost: '$colorPrimary',
  filled: '$colorInverse',
}

export const IconButton = IconButtonFrame.styleable(({ variant, children, ...props }, ref) => {
  return (
    <IconButtonFrame
      ref={ref}
      variant={variant}
      transition="quick"
      animateOnly={['opacity', 'transform', 'background-color']}
      {...props}
    >
      {children}
    </IconButtonFrame>
  )
})
